import {
  useState,
  useEffect,
  Suspense,
  memo,
  lazy,
  type ReactNode,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import type { RoarNotification } from '../device-link/DeviceDashboard'

const RoarAnimation = lazy(() => import('./RoarAnimation'))
import { Loader, Stack, Table, Text } from '@mantine/core'
import { BarChart } from '@mantine/charts'
import { Typography } from '../common/typography'
import { useCompetitionId } from '../common/hooks'
import { handleError } from '../common/error-handler'
import { useCompetition } from '../common/components/header/api'
import RowProgress from '../stories/RowProgress/RowProgress'
import RingProgressCard from '../stories/RingProgressCard/RingProgressCard'
import classes from './Dashboard.module.css'
import { useMetrics, useMetricsData, getMaxPeriod } from '../widgets/api'
import { useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useUserData } from '../auth/api'
import { LeaguestarState } from '../state'
import { useFmtColor, FmtColored } from '../widgets/format'
import { DashboardChrome } from './DashboardChrome'
import { DashboardRenderer } from './DashboardRenderer'
import {
  useCompetitionDashboard,
  useDashboardById,
  useDeviceDashboardConfig,
} from './api'
import { useParams } from 'react-router'
import { CompetitionIdProvider } from '../common/hooks'

// Wrapper component to format KPI values
function FormattedRowProgress({
  metricKey,
  title,
  rawValue,
}: {
  metricKey: string
  title: string
  rawValue: number
}) {
  const color = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic: 'value',
    value: rawValue,
  })

  return <RowProgress title={title} value={rawValue} color={color} />
}

function FormattedRingProgressCard({
  metricKey,
  title,
  rawValue,
}: {
  metricKey: string
  title: string
  rawValue: number
}) {
  const color = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic: 'value',
    value: rawValue,
  })

  // Convert to percentage for the progress bar (0-100 scale)
  const percentValue = rawValue * 100

  return <RingProgressCard title={title} value={percentValue} color={color} />
}

function Page1WithData() {
  const competitionId = useCompetitionId()

  // Fetch all metrics and filter for KPIs
  const { data: allMetrics } = useMetrics()
  const kpis = allMetrics.filter((m) => m.is_kpi)

  // Use competition start date for total period (extract yyyy-mm-dd from ISO timestamp)
  const totalPeriod = '2025-01-01'

  // Fetch max period for month
  const { data: maxPeriodData } = useSuspenseQuery({
    queryKey: ['max-period', 'month', 'overall', competitionId],
    queryFn: () =>
      getMaxPeriod({
        period_type: 'month',
        subject_type: 'overall',
        competition_id: competitionId,
      }),
  })

  const monthPeriod = maxPeriodData.max_period || totalPeriod

  // Fetch KPI data for total period, overall subject
  const { data: totalDataPoints } = useMetricsData({
    period_type: 'total',
    period_range: false,
    periods: [totalPeriod],
    subject_type: 'overall',
    subject_ids: [0],
    metrics: kpis.map((k) => k.key),
    components: kpis.map(() => 'percent'),
    statistics: kpis.map(() => 'value'),
    competition_id: competitionId,
  })

  // Fetch KPI data for month period, overall subject
  const { data: monthDataPoints } = useMetricsData({
    period_type: 'month',
    period_range: false,
    periods: [monthPeriod],
    subject_type: 'overall',
    subject_ids: [0],
    metrics: kpis.map((k) => k.key),
    components: kpis.map(() => 'percent'),
    statistics: kpis.map(() => 'value'),
    competition_id: competitionId,
  })

  // Transform total data: map metric IDs to values
  const totalMetricValuesMap = new Map<number, number>()
  totalDataPoints.forEach((point) => {
    totalMetricValuesMap.set(point.metric, point.value)
  })

  // Transform month data: map metric IDs to values
  const monthMetricValuesMap = new Map<number, number>()
  monthDataPoints.forEach((point) => {
    monthMetricValuesMap.set(point.metric, point.value)
  })

  // Create array with metric info and raw values for formatting (total data)
  const totalKpiData = kpis
    .map((metric) => ({
      metricKey: metric.key,
      metricId: metric.metric_id,
      title: metric.short_name || metric.name,
      rawValue: totalMetricValuesMap.get(metric.metric_id) || 0,
    }))
    .filter((item) => item.rawValue > 0) // Filter out KPIs with no data

  // Create array with metric info and raw values for formatting (month data)
  const monthKpiData = kpis
    .map((metric) => ({
      metricKey: metric.key,
      metricId: metric.metric_id,
      title: metric.short_name || metric.name,
      rawValue: monthMetricValuesMap.get(metric.metric_id) || 0,
    }))
    .filter((item) => item.rawValue > 0) // Filter out KPIs with no data

  // Use total KPIs for first section, month KPIs for second section
  const rowData = totalKpiData
  const ringData = monthKpiData

  // Format month name
  const monthName = dayjs(monthPeriod).format('MMMM')

  // Show message if no data available
  if (totalKpiData.length === 0 && monthKpiData.length === 0) {
    return (
      <div className={classes.page}>
        <div className={classes.pageWrapper}>
          <Typography
            fs="xxxl"
            fw={700}
            lh="75"
            level={2}
            fc={{ light: 'gray-3', dark: 'gray-3' }}
          >
            No KPI Data Available
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.pageWrapper}>
        <Typography
          fs="xxxl"
          fw={700}
          lh="75"
          level={2}
          fc={{ light: 'gray-3', dark: 'gray-3' }}
        >
          Total KPI Performance
        </Typography>
        <div className={classes.progressSection}>
          {rowData.map((item, index) => (
            <FormattedRowProgress
              key={index}
              metricKey={item.metricKey}
              title={item.title}
              rawValue={item.rawValue}
            />
          ))}
        </div>
      </div>
      <div className={classes.pageWrapper}>
        <Typography
          fs="xxxl"
          fw={700}
          lh="75"
          level={2}
          fc={{ light: 'gray-3', dark: 'gray-3' }}
        >
          {monthName} Overview
        </Typography>
        <div className={classes.ringProgressSection}>
          {ringData.map((item, index) => (
            <FormattedRingProgressCard
              key={index}
              metricKey={item.metricKey}
              title={item.title}
              rawValue={item.rawValue}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function Page2WithData() {
  const competitionId = useCompetitionId()
  const { data: competition } = useCompetition(competitionId)

  // Fetch all metrics and get first KPI
  const { data: allMetrics } = useMetrics()
  const kpis = allMetrics.filter((m) => m.is_kpi)
  const firstKpi = kpis[0]

  if (!firstKpi) {
    return (
      <div className={classes.page}>
        <div className={classes.pageWrapper}>
          <Typography
            fs="xxxl"
            fw={700}
            lh="75"
            level={2}
            fc={{ light: 'gray-3', dark: 'gray-3' }}
          >
            No KPI Data Available
          </Typography>
        </div>
      </div>
    )
  }

  // Get competition year from start date
  const competitionYear = competition.starts_at
    ? dayjs(competition.starts_at).year()
    : dayjs().year()

  // Generate 12 months for the competition year
  const months = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0')
    return {
      period: `${competitionYear}-${month}-01`,
      label: dayjs(`${competitionYear}-${month}-01`).format('MMM'),
    }
  })

  // Fetch monthly value data for chart
  const { data: monthlyValueData } = useMetricsData({
    period_type: 'month',
    period_range: false,
    periods: months.map((m) => m.period),
    subject_type: 'overall',
    subject_ids: [0],
    metrics: months.map(() => firstKpi.key),
    components: months.map(() => 'percent'),
    statistics: months.map(() => 'value'),
    competition_id: competitionId,
  })

  // Fetch YTD data for table
  const { data: monthlyYtdData } = useMetricsData({
    period_type: 'month',
    period_range: false,
    periods: months.map((m) => m.period),
    subject_type: 'overall',
    subject_ids: [0],
    metrics: months.map(() => firstKpi.key),
    components: months.map(() => 'percent'),
    statistics: months.map(() => 'value_ytd'),
    competition_id: competitionId,
  })

  // Map monthly values by period
  const valueByPeriod = new Map<string, number>()
  monthlyValueData.forEach((point) => {
    valueByPeriod.set(point.period, point.value)
  })

  // Map YTD values by period
  const ytdByPeriod = new Map<string, number>()
  monthlyYtdData.forEach((point) => {
    ytdByPeriod.set(point.period, point.value)
  })

  // Prepare table data (YTD values)
  const tableData = months.map((month) => ({
    label: month.label,
    period: month.period,
    value: ytdByPeriod.get(month.period) || null,
  }))

  const kpiName = firstKpi.short_name || firstKpi.name
  const longKpiName = firstKpi.name

  // Prepare chart data (monthly values) - key must match the series name
  const chartData = months.map((month) => {
    const value = valueByPeriod.get(month.period)
    return {
      label: month.label.toUpperCase(),
      [kpiName]: value != null ? value * 100 : null, // Convert to percentage (0-100 scale)
    }
  })

  // Calculate max value for chart domain
  const maxValue = Math.max(
    ...chartData
      .map((d) => d[kpiName])
      .filter((v): v is number => v !== null && typeof v === 'number')
  )
  const chartDomain = Math.ceil(maxValue / 100) * 100 || 100 // Round up to next 100, default to 100

  return (
    <div className={classes.page}>
      <div className={classes.pageWrapper}>
        <Typography
          fs="xxxl"
          fw={700}
          lh="75"
          level={2}
          fc={{ light: 'gray-3', dark: 'gray-3' }}
        >
          {kpiName} YTD
        </Typography>
        <div className={classes.progressSection}>
          <Table
            styles={{
              table: { backgroundColor: 'transparent' },
              td: {
                padding: '10px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Table.Tbody>
              {tableData.map((row, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td style={{ fontSize: '24px', color: '#dee2e6' }}>
                    {dayjs(row.period).format('MMMM')}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontSize: '24px' }}>
                    {row.value ? (
                      <FmtColored
                        value={row.value}
                        metric={firstKpi.key}
                        component="percent"
                        statistic="value_ytd"
                      />
                    ) : (
                      <span style={{ fontSize: '24px', color: '#dee2e6' }}>
                        -
                      </span>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </div>
      <div className={classes.pageWrapper}>
        <Typography
          fs="xxxl"
          fw={700}
          lh="75"
          level={2}
          fc={{ light: 'gray-3', dark: 'gray-3' }}
        >
          {longKpiName} Monthly Performance
        </Typography>
        <div className={classes.graphSection}>
          <BarChart
            h="100%"
            data={chartData}
            dataKey="label"
            series={[{ name: kpiName, color: 'teal.5' }]}
            withLegend={false}
            gridAxis="x"
            withTooltip={false}
            barProps={{ barSize: 30 }}
            yAxisProps={{
              domain: [0, chartDomain],
              style: { fontSize: 24, fill: 'white' },
              axisLine: false,
            }}
            xAxisProps={{
              style: { fontSize: 24, fill: 'white' },
              interval: 0,
              padding: { left: 20, right: 20 },
              axisLine: false,
              tickLine: false,
            }}
            valueFormatter={(value) =>
              value != null ? `${value.toFixed(1)}%` : '-'
            }
          />
        </div>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { data: user } = useUserData(location.pathname)
  useEffect(() => {
    LeaguestarState.update((state) => {
      state.user_id = user.user_id
      state.platforms = user.platforms
    })
  }, [user])

  const userId = LeaguestarState.useState((state) => state.user_id)

  if (userId) {
    return <Dashboard />
  } else {
    return null
  }
}

/**
 * Routed page for `/dashboards/:dashboardId/preview`. Loads the dashboard by
 * its own id (not by competition), provides the dashboard's `competition_id`
 * to child widgets via `CompetitionIdProvider`, then renders it.
 */
export function DashboardPreviewByIdPage() {
  const { data: user } = useUserData(location.pathname)
  const { dashboardId: dashboardIdParam } = useParams()
  const dashboardId = dashboardIdParam ? parseInt(dashboardIdParam, 10) : 0

  useEffect(() => {
    LeaguestarState.update((state) => {
      state.user_id = user.user_id
      state.platforms = user.platforms
    })
  }, [user])

  const { data: dashboardConfig, isLoading } = useDashboardById(dashboardId)

  const userId = LeaguestarState.useState((state) => state.user_id)

  if (!userId || isLoading) return null
  if (!dashboardConfig) return null
  if (!dashboardConfig.config?.slides?.length) return null

  return (
    <CompetitionIdProvider competitionId={dashboardConfig.competition_id}>
      <DashboardRenderer config={dashboardConfig.config} />
    </CompetitionIdProvider>
  )
}

export const Dashboard = memo(function Dashboard({
  roarNotification,
  onRoarDone,
  /**
   * When true, fetch the dashboard config bound to the current device token
   * (instead of looking up "any dashboard for this competition"). Use from
   * the /tv (device-link) flow where we want exactly the dashboard the device
   * was paired with.
   */
  useDeviceConfig = false,
}: {
  roarNotification?: RoarNotification | null
  onRoarDone?: () => void
  useDeviceConfig?: boolean
}) {
  const competitionId = useCompetitionId()
  const competitionQuery = useCompetitionDashboard(
    useDeviceConfig ? 0 : competitionId
  )
  const deviceQuery = useDeviceDashboardConfig()
  const { data: dashboardConfig, isLoading } = useDeviceConfig
    ? deviceQuery
    : competitionQuery

  // Show nothing while initial fetch is in flight; the chrome would flash empty otherwise.
  if (isLoading) return null

  const overlay = roarNotification && (
    <Suspense fallback={null}>
      <RoarAnimation
        text={roarNotification.text}
        authorName={roarNotification.author_name}
        authorAvatar={roarNotification.author_avatar}
        onDone={onRoarDone}
      />
    </Suspense>
  )

  if (dashboardConfig?.config?.slides?.length) {
    return (
      <>
        <DashboardRenderer config={dashboardConfig.config} />
        {overlay}
      </>
    )
  }

  return (
    <>
      <FallbackHardCodedDashboard />
      {overlay}
    </>
  )
})

/**
 * Original hard-coded dashboard layout, used as a fallback for competitions
 * that don't have a saved Dashboard yet.
 */
/**
 * Same isolation as the configured dashboard's widgets: a failing page must
 * not throw past Suspense and take the whole dashboard down.
 */
function PageBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Stack w="100%" h="100%" justify="center" align="center">
          <Text c="dimmed">Unavailable</Text>
        </Stack>
      )}
      onError={handleError}
    >
      <Suspense
        fallback={
          <Stack w="100%" h="100%" justify="center" align="center">
            <Loader color="#0B6B7B" />
          </Stack>
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function FallbackHardCodedDashboard() {
  const [currentPage, setCurrentPage] = useState(0)
  const [progress, setProgress] = useState(0)

  const pageCount = 2

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % pageCount)
      setProgress(0)
    }, 10500)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 1, 100))
    }, 100)
    return () => clearInterval(interval)
  }, [currentPage])

  return (
    <DashboardChrome
      slideCount={pageCount}
      currentSlide={currentPage}
      slideProgress={progress}
    >
      {currentPage === 0 ? (
        <PageBoundary>
          <Page1WithData />
        </PageBoundary>
      ) : (
        <PageBoundary>
          <Page2WithData />
        </PageBoundary>
      )}
    </DashboardChrome>
  )
}
